import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card, CardMedia, CardContent, Button, InputAdornment, IconButton, Pagination, CircularProgress, Select, MenuItem, FormControl } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { request } from "app/network";
import { useQuery } from "@tanstack/react-query";
import MDInput from "components/MDInput"; // Import MDInput
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";

interface ExternalGoodsSearchDialogProps {
    open: boolean;
    onClose: () => void;
}

interface GoodsItem {
    goodsCode: string;
    goodsName: string;
    brandName: string;
    salePrice: number;
    realPrice: number;
    goodsImgS: string;
    isRegistered?: boolean;
}

const ExternalGoodsSearchDialog = ({ open, onClose }: ExternalGoodsSearchDialogProps) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [goods, setGoods] = useState<GoodsItem[]>([]);

    // Debounce Logic
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedKeyword(searchTerm);
        }, 500); // 500ms delay

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    // Reset state when dialog is closed
    useEffect(() => {
        if (!open) {
            setSearchTerm("");
            setDebouncedKeyword("");
            setGoods([]);
        }
    }, [open]);

    // Fetch Goods using TanStack Query v5
    const query = useQuery({
        queryKey: ["externalGoods", debouncedKeyword],
        queryFn: async () => {
            // Updated limit to 60 as requested
            const response = await request("Giftishow.GetGoodsList", {
                paramz: { start: 1, size: 60, keyword: debouncedKeyword }
            });
            return response.data?.result?.goodsList || [];
        },
        enabled: open && !!debouncedKeyword // Only enable if keyword exists
    });

    // Sync data to local state for rendering
    useEffect(() => {
        if (query.data) {
            setGoods(query.data as GoodsItem[]);
        }
    }, [query.data]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<GoodsItem | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<number | string>("");
    const [categories, setCategories] = useState<any[]>([]);

    // Fetch Categories
    const categoryQuery = useQuery({
        queryKey: ["giftCategories"],
        queryFn: async () => {
            const response = await request("GiftCategory.GetList", {});
            return response.data || [];
        },
        enabled: open // Fetch when dialog opens
    });

    useEffect(() => {
        if (categoryQuery.data) {
            setCategories(categoryQuery.data);
            if (categoryQuery.data.length > 0) {
                // Default to first category if available
                setSelectedCategory(categoryQuery.data[0].seqId);
            }
        }
    }, [categoryQuery.data]);

    const [requiredPoint, setRequiredPoint] = useState<string>("");

    const handleImportClick = (item: GoodsItem) => {
        setSelectedItem(item);
        // Default required point to salePrice or realPrice
        setRequiredPoint(item.salePrice ? String(item.salePrice) : (item.realPrice ? String(item.realPrice) : "0"));
        setConfirmOpen(true);
    };

    const handleAdd = async () => {
        if (!selectedItem) return;
        if (!selectedCategory) {
            Swal.fire("오류", "카테고리를 선택해주세요.", "warning");
            return;
        }

        try {
            await request("Giftishow.AddDisplayItem", {
                paramz: {
                    goodsCode: selectedItem.goodsCode,
                    categorySeqId: Number(selectedCategory),
                    requiredPoint: requiredPoint ? Number(requiredPoint) : undefined
                }
            });

            Swal.fire("성공", "상품이 전시 목록에 추가되었습니다.", "success");
            setConfirmOpen(false);
            query.refetch(); // Refresh list to update "IsRegistered" status
            // Optional: Close main dialog too? User didn't specify. Keeping open allows adding more.
        } catch (error: any) {
            console.error("Add failed", error);
            Swal.fire("실패", error.message || "상품 추가 중 오류가 발생했습니다.", "error");
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{
                sx: { borderRadius: "16px", padding: 2, background: darkMode ? "#1a2035" : "#f8f9fa" }
            }}>
                <DialogTitle sx={{ pb: 1, pl: 2 }}>
                    <MDTypography variant="h5" fontWeight="bold">기프티쇼 비즈 상품 검색</MDTypography>
                </DialogTitle>

                <DialogContent sx={{ p: 2 }}>
                    <MDBox mb={3} sx={{ background: darkMode ? "#202940" : "#ffffff", p: 2, borderRadius: "12px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)" }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <MDInput
                                    fullWidth
                                    placeholder="상품명 또는 브랜드명을 입력하세요..."
                                    value={searchTerm}
                                    onChange={(e: any) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: "text.secondary" }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: "8px", pl: 1 }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MDTypography variant="caption" color="text" sx={{ display: 'block', lineHeight: 1.5, pl: 1 }}>
                                    ✨ <strong>자동 검색</strong>이 활성화되어 있습니다.<br />
                                    <span style={{ opacity: 0.7 }}>키워드를 입력하면 잠시 후 자동으로 검색됩니다.<br />(최대 60개까지 표시됩니다)</span>
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </MDBox>

                    <MDBox height="65vh" overflow="auto" sx={{ pr: 1, pt: 2 }}>
                        {!debouncedKeyword ? (
                            <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" opacity={0.6}>
                                <SearchIcon sx={{ fontSize: 80, mb: 3, color: "text.disabled" }} />
                                <MDTypography variant="h6" color="text" fontWeight="regular">
                                    상품을 검색하여 목록을 불러오세요.
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <Grid container spacing={3}>
                                {query.isLoading && (
                                    <Grid item xs={12}>
                                        <MDBox p={8} display="flex" justifyContent="center" alignItems="center">
                                            <CircularProgress size={50} color="info" />
                                        </MDBox>
                                    </Grid>
                                )}

                                {query.isError && (
                                    <Grid item xs={12}>
                                        <MDBox p={4} textAlign="center" color="error">
                                            데이터를 불러오는 중 오류가 발생했습니다.
                                        </MDBox>
                                    </Grid>
                                )}

                                {!query.isLoading && !query.isError && goods.length === 0 && (
                                    <Grid item xs={12}>
                                        <MDBox p={6} textAlign="center">
                                            <MDTypography variant="h6" color="text">
                                                검색 결과가 없습니다.
                                            </MDTypography>
                                        </MDBox>
                                    </Grid>
                                )}

                                {!query.isLoading && goods.map((item) => (
                                    <Grid item xs={12} sm={6} md={3} lg={2.4} key={item.goodsCode}>
                                        <Card sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: "12px",
                                            overflow: "hidden", // Ensure image clips to border radius
                                            boxShadow: item.isRegistered ? "none" : "0px 4px 6px rgba(0,0,0,0.05)",
                                            border: item.isRegistered ? "none" : "1px solid rgba(0,0,0,0.05)",
                                            opacity: item.isRegistered ? 0.6 : 1,
                                            backgroundColor: item.isRegistered ? (darkMode ? "#2c3e50" : "#f0f0f0") : undefined,
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": item.isRegistered ? {} : {
                                                transform: "translateY(-5px)",
                                                boxShadow: "0px 12px 20px rgba(0,0,0,0.1)",
                                                cursor: "pointer",
                                                borderColor: "#1A73E8"
                                            }
                                        }} onClick={() => {
                                            if (item.isRegistered) return;
                                            handleImportClick(item);
                                        }}>
                                            <MDBox position="relative" sx={{ height: "160px", backgroundColor: "#fff", p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                <CardMedia
                                                    component="img"
                                                    image={item.goodsImgS}
                                                    alt={item.goodsName}
                                                    sx={{
                                                        maxWidth: "100%",
                                                        maxHeight: "100%",
                                                        width: "auto",
                                                        height: "auto",
                                                        objectFit: "contain",
                                                        filter: item.isRegistered ? "grayscale(100%)" : "none"
                                                    }}
                                                />
                                                <MDBox
                                                    position="absolute"
                                                    top={10}
                                                    left={10}
                                                    sx={{
                                                        backgroundColor: "rgba(50, 50, 50, 0.85)", // Softer dark gray
                                                        padding: "2px 6px",
                                                        borderRadius: "4px",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                        zIndex: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    <MDTypography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#ffffff",
                                                            fontSize: "11px",
                                                            fontWeight: 700,
                                                            lineHeight: 1,
                                                            letterSpacing: "0.5px"
                                                        }}
                                                    >
                                                        {item.brandName}
                                                    </MDTypography>
                                                </MDBox>

                                                {/* Registered Badge */}
                                                {item.isRegistered && (
                                                    <MDBox
                                                        position="absolute"
                                                        top="50%"
                                                        left="50%"
                                                        sx={{
                                                            transform: "translate(-50%, -50%)",
                                                            width: "auto",
                                                            textAlign: "center",
                                                            bgcolor: "rgba(0,0,0,0.7)",
                                                            px: 3,
                                                            py: 1,
                                                            borderRadius: "20px",
                                                            whiteSpace: "nowrap"
                                                        }}
                                                    >
                                                        <MDTypography variant="subtitle2" color="white" fontWeight="bold">
                                                            등록됨
                                                        </MDTypography>
                                                    </MDBox>
                                                )}

                                            </MDBox>

                                            <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 2 }}>
                                                <MDTypography variant="subtitle2" sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    height: "2.6em",
                                                    mb: 1,
                                                    lineHeight: 1.3
                                                }}>
                                                    {item.goodsName}
                                                </MDTypography>

                                                <MDBox mt="auto" display="flex" alignItems="center" justifyContent="space-between">
                                                    <MDTypography variant="h6" color="info" fontWeight="bold">
                                                        {Number(item.realPrice).toLocaleString()}
                                                        <MDTypography component="span" variant="caption" color="text">원</MDTypography>
                                                    </MDTypography>
                                                </MDBox>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: "center" }}>
                    <Button onClick={onClose} variant="text" color="secondary" size="large">
                        닫기
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", p: 2 } }}>
                <DialogTitle>
                    <MDTypography variant="h5" fontWeight="bold">상품 추가</MDTypography>
                </DialogTitle>
                <DialogContent>
                    {selectedItem && (
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            <MDBox display="flex" alignItems="center" gap={2} p={2} border="1px solid #eee" borderRadius="lg">
                                <MDBox component="img" src={selectedItem.goodsImgS} width="80px" borderRadius="md" />
                                <MDBox>
                                    <MDTypography variant="body2" color="text">{selectedItem.brandName}</MDTypography>
                                    <MDTypography variant="h6">{selectedItem.goodsName}</MDTypography>
                                    <MDTypography variant="body2" fontWeight="bold">{selectedItem.realPrice?.toLocaleString()}원</MDTypography>
                                </MDBox>
                            </MDBox>

                            <MDBox>
                                <MDTypography variant="caption" fontWeight="bold" mb={1} display="block">필요 포인트 (판매가)</MDTypography>
                                <MDInput
                                    fullWidth
                                    type="number"
                                    value={requiredPoint}
                                    onChange={(e: any) => setRequiredPoint(e.target.value)}
                                    placeholder="포인트를 입력하세요"
                                />
                            </MDBox>

                            <MDBox>
                                <MDTypography variant="caption" fontWeight="bold" mb={1} display="block">카테고리 선택</MDTypography>
                                <FormControl fullWidth sx={{ mt: 1 }}>
                                    <Select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        displayEmpty
                                        inputProps={{ "aria-label": "Category Select" }}
                                        sx={{
                                            height: 45,
                                            "& .MuiSelect-icon": {
                                                color: darkMode ? "#ffffff !important" : "#000000 !important",
                                                display: "block !important",
                                                right: "10px",
                                                position: "absolute",
                                                fontSize: "1.5rem !important"
                                            }
                                        }}
                                    >
                                        <MenuItem value="" disabled>카테고리를 선택하세요</MenuItem>
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.seqId} value={cat.seqId}>{cat.categoryName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </MDBox>
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="secondary">취소</Button>
                    <MDButton variant="gradient" color="info" onClick={handleAdd}>추가하기</MDButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ExternalGoodsSearchDialog;
